import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router";
import {
  Alert,
  Button,
  Card,
  Divider,
  Dropdown,
  Empty,
  Popconfirm,
  Progress,
  Skeleton,
  Tag,
  Typography,
  Space,
  message,
} from "antd";
import {
  EditOutlined,
  LockOutlined,
  MoreOutlined,
  PlusOutlined,
  RightOutlined,
  SendOutlined,
  TeamOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useAPI } from "../../hooks/useAPI";
import { ENDPOINTS } from "../../constants/endpoints.constant";
import { getCall } from "../../constants/apicalls.constant";
import { useBib, rangeKey } from "./hooks/useBib";
import { isEventPastOrArchived } from "../../utils/eventDisplay";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";

import DistanceFilterCards from "./components/DistanceFilterCards";
import TicketFilter, { TicketOption } from "./components/TicketFilter";
import BibTable from "./components/BibTable";
import BibRangeModal from "./components/BibRangeModal";

const { Text, Title } = Typography;

const BibIndex = () => {
  const { id: eventId } = useParams();

  const [selectedDistance, setSelectedDistance] = useState<string | undefined>(
    undefined,
  );
  const [selectedTicket, setSelectedTicket] = useState<
    TicketOption | undefined
  >(undefined);
  const [rangeModalOpen, setRangeModalOpen] = useState(false);
  const [manualMode, setManualMode] = useState(false);

  const tableRef = useRef<HTMLDivElement>(null);

  const {
    distanceGroups,
    loading,
    assigning,
    authorizing,
    savedRanges,
    total,
    currentPage,
    pageSize,
    fetchData,
    autoAssign,
    manualAssign,
    authorizeAll,
    onRangeSaved,
    loadSavedRanges,
  } = useBib();

  const { response: eventResponse, loading: eventLoading } = useAPI<any>(
    ENDPOINTS.event,
    true,
    [eventId],
    getCall({}, eventId),
  );

  const storeEvent = useSelector((state: RootState) => state.event);
  const event = eventResponse?.data;
  const readOnlyAssignments =
    isEventPastOrArchived(storeEvent) || isEventPastOrArchived(event);

  const distances = useMemo<string[]>(() => {
    const tickets: any[] = event?.tickets || [];
    const seen = new Set<string>();
    tickets.forEach((t) => {
      if (t.distance) seen.add(t.distance);
    });
    return Array.from(seen);
  }, [event]);

  // Include registration end date/time so we can gate assignment
  const allTickets = useMemo<TicketOption[]>(() => {
    const tickets: any[] = event?.tickets || [];
    return tickets
      .filter((t) => t.id && t.name && t.distance)
      .map((t) => ({
        id: t.id,
        name: t.name,
        distance: t.distance,
        registration_end_date: t.registration_end_date,
        registration_end_time: t.registration_end_time,
      }));
  }, [event]);

  const ticketsForDistance = useMemo(
    () =>
      selectedDistance
        ? allTickets.filter((t) => t.distance === selectedDistance)
        : [],
    [allTickets, selectedDistance],
  );

  // ─── Registration still open? Block assignment until it closes ────────────────
  const isRegistrationOpen = useMemo(() => {
    if (!selectedTicket?.registration_end_date) return false;
    const endDateTime = dayjs(
      `${selectedTicket.registration_end_date} ${selectedTicket.registration_end_time || "23:59:59"}`,
    );
    return endDateTime.isValid() && dayjs().isBefore(endDateTime);
  }, [selectedTicket]);

  const registrationClosesAt = useMemo(() => {
    if (!selectedTicket?.registration_end_date) return null;
    const endDateTime = dayjs(
      `${selectedTicket.registration_end_date} ${selectedTicket.registration_end_time || "23:59:59"}`,
    );
    return endDateTime.isValid()
      ? endDateTime.format("DD MMM YYYY, hh:mm A")
      : null;
  }, [selectedTicket]);

  // ─── Reset on event change ────────────────────────────────────────────────────
  useEffect(() => {
    setSelectedDistance(undefined);
    setSelectedTicket(undefined);
    setManualMode(false);
  }, [eventId]);

  // ─── Auto-select first distance when available ────────────────────────────────
  useEffect(() => {
    if (distances.length > 0 && !selectedDistance) {
      setSelectedDistance(distances[0]);
    }
  }, [distances, selectedDistance]);

  // ─── Auto-select first ticket when distance is set ────────────────────────────
  useEffect(() => {
    if (ticketsForDistance.length > 0 && !selectedTicket) {
      setSelectedTicket(ticketsForDistance[0]);
    }
  }, [ticketsForDistance, selectedTicket]);

  // ─── Fetch registrations when ticket changes ──────────────────────────────────
  useEffect(() => {
    if (eventId && selectedDistance && selectedTicket) {
      fetchData(eventId, selectedDistance, 1, selectedTicket.id);
    }
  }, [eventId, selectedDistance, selectedTicket, fetchData]);

  // ─── Handlers ─────────────────────────────────────────────────────────────────
  const handleDistanceSelect = (distance: string | undefined) => {
    setSelectedDistance(distance);
    setSelectedTicket(undefined);
    setManualMode(false);
  };

  const handleTicketSelect = (ticket_id: string | undefined) => {
    const ticket = allTickets.find((t) => t.id === ticket_id);
    setSelectedTicket(ticket);
    setManualMode(false);
  };

  const handlePageChange = (page: number) => {
    if (eventId && selectedDistance && selectedTicket) {
      fetchData(eventId, selectedDistance, page, selectedTicket.id);
    }
  };

  useEffect(() => {
    if (eventId && allTickets.length > 0) {
      loadSavedRanges(eventId, allTickets);
    }
  }, [eventId, allTickets, loadSavedRanges]);

  const handleRangeSaved = (key: string, rangeInfo: any) => {
    onRangeSaved(key, rangeInfo);
    const distance = key.includes("___") ? key.split("___")[0] : key;
    allTickets
      .filter((t) => t.distance === distance)
      .forEach((t) => {
        onRangeSaved(rangeKey(distance, t.id), {
          ...rangeInfo,
          ticket_id: t.id,
          ticket_name: t.name,
        });
      });
  };

  const handleManualAssignClick = () => {
    if (!readyToLoad) {
      message.info("Please select a distance and ticket first.");
      return;
    }
    if (isRegistrationOpen) {
      message.warning(
        "BIB assignment is only available after registration closes.",
      );
      return;
    }
    setManualMode(true);
    setTimeout(() => {
      tableRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  };

  // ─── Derived state ────────────────────────────────────────────────────────────
  const readyToLoad = !!(selectedDistance && selectedTicket);

  const currentRangeKey =
    selectedDistance && selectedTicket
      ? rangeKey(selectedDistance, selectedTicket.id)
      : "";
  // const existingRangeForDistance = useMemo(() => {
  //   if (!selectedDistance) return undefined;
  //   if (savedRanges[currentRangeKey]) return savedRanges[currentRangeKey];
  //   if (savedRanges[selectedDistance]) return savedRanges[selectedDistance];
  //   const match = Object.entries(savedRanges).find(([k]) =>
  //     k.startsWith(`${selectedDistance}___`),
  //   );
  //   return match?.[1];
  // }, [savedRanges, selectedDistance, currentRangeKey]);

  const hasRangeForSelected = !!savedRanges[currentRangeKey];
  const isAssigning = assigning === currentRangeKey;

  const registrations = distanceGroups[0]?.registrations ?? [];

  const assignedCount = useMemo(
    () => distanceGroups.reduce((sum, g) => sum + g.assigned, 0),
    [distanceGroups],
  );
  const unassignedCount = total > 0 ? total - assignedCount : 0;
  const assignPercent =
    total > 0 ? Math.round((assignedCount / total) * 100) : 0;

  const savedRangeTicketIds = useMemo(
    () =>
      Object.keys(savedRanges)
        .filter(
          (k) => selectedDistance && k.startsWith(`${selectedDistance}___`),
        )
        .map((k) => k.replace(`${selectedDistance}___`, "")),
    [savedRanges, selectedDistance],
  );

  const bibAssignedParticipants = useMemo(
    () =>
      distanceGroups
        .flatMap((g) => g.registrations)
        .filter((p) => p.bib_number),
    [distanceGroups],
  );

  // "published" = every participant with a BIB has status "authorized"
  const allPublished = useMemo(
    () =>
      bibAssignedParticipants.length > 0 &&
      bibAssignedParticipants.every((p) => p.bib_status === "authorized"),
    [bibAssignedParticipants],
  );

  const currentSavedRange = hasRangeForSelected
    ? savedRanges[currentRangeKey]
    : undefined;
  const existingBibs = useMemo(
    () => registrations.filter((r) => r.bib_number).map((r) => r.bib_number!),
    [registrations],
  );

  return (
    <div
      style={{ backgroundColor: "#f8fafc", minHeight: "100vh" }}
      className="p-16 lg:p-10"
    >
      {/* ── Page Header ───────────────────────────────────────────────────────── */}
      <div
        style={{ backgroundColor: "#fff", borderBottom: "1px solid #f1f5f9" }}
      >
        <div className="px-4 py-5 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-start">
            <div>
              {eventLoading ? (
                <Skeleton.Input active size="large" style={{ width: 240 }} />
              ) : (
                <Title level={3} className="mb-0 leading-tight">
                  {event?.name || "BIB Assignment"}
                </Title>
              )}
            </div>

            {!readOnlyAssignments && (
              <Space wrap>
                {/* Always visible */}
                <Button
                  icon={<PlusOutlined />}
                  disabled={distances.length === 0}
                  onClick={() => setRangeModalOpen(true)}
                >
                  Create BIB Range
                </Button>

                {/* Auto Assign — only when range set, registration closed, not published */}
                {readyToLoad && hasRangeForSelected && !allPublished && (
                  <Button
                    type="default"
                    icon={<ThunderboltOutlined />}
                    loading={isAssigning}
                    disabled={isRegistrationOpen}
                    title={
                      isRegistrationOpen
                        ? `Registration closes ${registrationClosesAt}`
                        : "Auto-assign BIB numbers"
                    }
                    onClick={() =>
                      eventId &&
                      selectedDistance &&
                      selectedTicket &&
                      autoAssign(eventId, selectedDistance, selectedTicket.id)
                    }
                  >
                    Auto Assign
                  </Button>
                )}

                {/* Manual Assign — only when selection made, registration closed, not published */}
                {readyToLoad && !allPublished && (
                  <Button
                    type="primary"
                    icon={<EditOutlined />}
                    disabled={isRegistrationOpen}
                    title={
                      isRegistrationOpen
                        ? `Registration closes ${registrationClosesAt}`
                        : undefined
                    }
                    onClick={handleManualAssignClick}
                  >
                    Manual Assign
                  </Button>
                )}

                {/* Published state indicator */}
                {allPublished && (
                  <Tag
                    color="green"
                    icon={<LockOutlined />}
                    style={{
                      padding: "5px 12px",
                      fontSize: 13,
                      borderRadius: 6,
                    }}
                  >
                    BIBs Published
                  </Tag>
                )}

                <Dropdown
                  menu={{ items: [{ key: "export", label: "Export to CSV" }] }}
                  trigger={["click"]}
                >
                  <Button icon={<MoreOutlined />} />
                </Dropdown>
              </Space>
            )}
          </div>
        </div>
      </div>

      {/* ── Main Content ──────────────────────────────────────────────────────── */}
      <div className="px-4 py-5 sm:px-6 lg:px-8">
        <div style={{ paddingTop: 8 }}>
          {/* Filter Card: Distance → Ticket */}
          <Card
            bordered={false}
            style={{
              marginBottom: 16,
              boxShadow: "0 1px 4px rgba(0,0,0,0.07)",
            }}
          >
            {eventLoading ? (
              <Skeleton active paragraph={{ rows: 2 }} />
            ) : (
              <>
                <DistanceFilterCards
                  distances={distances}
                  selected={selectedDistance}
                  onSelect={handleDistanceSelect}
                  savedRanges={savedRanges}
                  totalByDistance={
                    selectedDistance && total > 0
                      ? { [selectedDistance]: total }
                      : {}
                  }
                />
                {selectedDistance && (
                  <>
                    <Divider style={{ margin: "16px 0" }} />
                    <TicketFilter
                      tickets={ticketsForDistance}
                      selected={selectedTicket?.id}
                      onSelect={handleTicketSelect}
                      savedRangeKeys={savedRangeTicketIds}
                    />
                  </>
                )}
              </>
            )}
          </Card>

          {/* Sticky breadcrumb */}
          {readyToLoad && (
            <div
              style={{
                position: "sticky",
                top: 0,
                zIndex: 20,
                backgroundColor: "#fff",
                border: "1px solid #f1f5f9",
                padding: "8px 14px",
                marginBottom: 16,
                display: "flex",
                alignItems: "center",
                gap: 6,
                flexWrap: "wrap",
                boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                borderRadius: 8,
              }}
            >
              <TeamOutlined style={{ color: "#6366f1", fontSize: 13 }} />
              <Tag color="blue" style={{ margin: 0 }}>
                {selectedDistance}
              </Tag>
              <RightOutlined style={{ fontSize: 9, color: "#9ca3af" }} />
              <Tag color="purple" style={{ margin: 0 }}>
                {selectedTicket?.name}
              </Tag>
              {hasRangeForSelected && currentSavedRange && (
                <>
                  <RightOutlined style={{ fontSize: 9, color: "#9ca3af" }} />
                  <Tag color="green" style={{ margin: 0 }}>
                    BIB {currentSavedRange.prefix ? `${currentSavedRange.prefix}-` : ""}{currentSavedRange.start}–{currentSavedRange.prefix ? `${currentSavedRange.prefix}-` : ""}{currentSavedRange.end}
                  </Tag>
                </>
              )}
              {isRegistrationOpen && registrationClosesAt && (
                <Tag color="orange" style={{ margin: 0 }}>
                  Registration closes {registrationClosesAt}
                </Tag>
              )}
            </div>
          )}

          {/* Progress bar */}
          {readyToLoad && (
            <Card
              bordered={false}
              style={{
                marginBottom: 16,
                boxShadow: "0 1px 4px rgba(0,0,0,0.07)",
              }}
            >
              <div style={{ flex: 1 }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "baseline",
                    marginBottom: 6,
                  }}
                >
                  <Text style={{ fontSize: 13 }}>
                    <Text strong style={{ color: "#16a34a", fontSize: 20 }}>
                      {assignedCount}
                    </Text>
                    <Text type="secondary"> / {total} assigned</Text>
                  </Text>
                  <Space size={12}>
                    {unassignedCount > 0 && (
                      <Text style={{ fontSize: 12, color: "#d97706" }}>
                        {unassignedCount} unassigned
                      </Text>
                    )}
                    {allPublished ? (
                      <Tag
                        color="green"
                        icon={<LockOutlined />}
                        style={{ margin: 0 }}
                      >
                        Published
                      </Tag>
                    ) : (
                      <Text style={{ fontSize: 12 }} type="secondary">
                        {assignPercent}%
                      </Text>
                    )}
                  </Space>
                </div>
                <Progress
                  percent={assignPercent}
                  strokeColor={allPublished ? "#16a34a" : "#3b82f6"}
                  trailColor="#e2e8f0"
                  showInfo={false}
                  size="small"
                />
              </div>
            </Card>
          )}

          {/* Registration still open — block assignment */}
          {isRegistrationOpen && readyToLoad && registrationClosesAt && (
            <Alert
              type="warning"
              showIcon
              message="Registration Period Not Yet Closed"
              description={`BIB assignment is only available after registration closes. This ticket's registration closes on ${registrationClosesAt}.`}
              style={{ marginBottom: 16 }}
            />
          )}

          {/* Range hint */}
          {!readOnlyAssignments && readyToLoad && !hasRangeForSelected && (
            <div style={{ marginBottom: 12, textAlign: "right" }}>
              <Button
                size="small"
                type="dashed"
                icon={<PlusOutlined />}
                onClick={() => setRangeModalOpen(true)}
              >
                Create range for this ticket
              </Button>
            </div>
          )}

          {/* Published Alert */}
          {allPublished && readyToLoad && (
            <Alert
              type="success"
              showIcon
              icon={<LockOutlined />}
              message="BIB Numbers Published"
              description="All BIB numbers are confirmed and locked. Participants have been notified with their BIB number by email."
              style={{ marginBottom: 16 }}
            />
          )}

          {/* Manual Mode Alert */}
          {!readOnlyAssignments &&
            manualMode &&
            readyToLoad &&
            !allPublished && (
              <Alert
                type="info"
                closable
                onClose={() => setManualMode(false)}
                message="Manual Assign Mode"
                description={
                  'Click a participant\'s BIB number or the "Assign BIB" button in any row to set or change their BIB.'
                }
                style={{ marginBottom: 16 }}
              />
            )}

          {/* Registration Table */}
          <div ref={tableRef}>
            {!readyToLoad ? (
              <Card
                bordered={false}
                style={{ boxShadow: "0 1px 4px rgba(0,0,0,0.07)" }}
              >
                <Empty
                  description={
                    selectedDistance
                      ? "Select a ticket above to load registrations."
                      : "Select a distance above to continue."
                  }
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  style={{ padding: "48px 0" }}
                />
              </Card>
            ) : (
              <Card
                bordered={false}
                style={{ boxShadow: "0 1px 4px rgba(0,0,0,0.07)" }}
                title={
                  bibAssignedParticipants.length > 0 && !readOnlyAssignments ? (
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: 8,
                      }}
                    >
                      <Text strong style={{ fontSize: 15 }}>
                        Registrations
                      </Text>
                      {!allPublished ? (
                        <Popconfirm
                          title="Publish all BIB assignments?"
                          description="BIB numbers will be locked permanently and each participant will be notified by email."
                          onConfirm={() => {
                            if (eventId && selectedDistance && selectedTicket) {
                              authorizeAll(
                                eventId,
                                selectedDistance,
                                selectedTicket.id,
                              );
                            }
                          }}
                          okText="Yes, Publish All"
                          cancelText="Cancel"
                        >
                          <Button
                            type="primary"
                            icon={<SendOutlined />}
                            loading={authorizing}
                            size="small"
                          >
                            Publish All
                          </Button>
                        </Popconfirm>
                      ) : (
                        <Tag color="green" icon={<LockOutlined />}>
                          All Published
                        </Tag>
                      )}
                    </div>
                  ) : undefined
                }
              >
                <BibTable
                  registrations={registrations}
                  loading={loading}
                  mode={
                    readOnlyAssignments || allPublished || isRegistrationOpen
                      ? "view"
                      : "assign"
                  }
                  onManualAssign={(reg_id, bib_number) => {
                    if (eventId && selectedDistance && selectedTicket) {
                      manualAssign(
                        reg_id,
                        bib_number,
                        eventId,
                        selectedDistance,
                        selectedTicket.id,
                      );
                    }
                  }}
                  total={total}
                  currentPage={currentPage}
                  pageSize={pageSize}
                  onPageChange={handlePageChange}
                  savedRange={
                    currentSavedRange
                      ? {
                          start: currentSavedRange.start,
                          end: currentSavedRange.end,
                          prefix: currentSavedRange.prefix,
                        }
                      : undefined
                  }
                  existingBibs={existingBibs}
                />
              </Card>
            )}
          </div>

        </div>
      </div>

      {/* ── BIB Range Modal ──────────────────────────────────────────────────── */}
      <BibRangeModal
        open={rangeModalOpen}
        onClose={() => setRangeModalOpen(false)}
        eventId={eventId}
        distances={distances}
        tickets={allTickets}
        defaultDistance={selectedDistance}
        defaultTicketId={selectedTicket?.id}
        savedRanges={savedRanges}
        onSuccess={handleRangeSaved}
      />
    </div>
  );
};

export default BibIndex;
